import Sparkline from "./Sparkline"

// The 4-up KPI row at the top of the dashboard. Fed by the parent's
// fetchMeScans() result ({ total_lifetime, code_count, daily, week_total,
// prev_week_total, top_code }). Pure presentation — no fetches.
//
// "Scans this week" is the emphasis card (coal surface + voltage sparkline +
// week-over-week trend pill); the other three are plain white cards. Collapses
// from 4-up to 2-up under 920px.

// Week-over-week percent change. No prior week → 100% when we have scans now,
// else 0 (so a brand-new account reads "+0%" rather than dividing by zero).
function trendPct(now, prev) {
  if (!prev) return now ? 100 : 0
  return Math.round(((now - prev) / prev) * 100)
}

function TrendIcon({ up }) {
  // Lucide trending-up / trending-down, inlined (the codebase has no shared
  // Icon component — it inlines paths, e.g. CodeRow).
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {up
        ? <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>
        : <><polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" /></>}
    </svg>
  )
}

function TrendPill({ now, prev }) {
  const up = now >= prev
  const value = trendPct(now, prev)
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold " +
        (up ? "bg-voltage-pale text-voltage-deep" : "bg-[#FFF1F2] text-[#BE123C]")
      }
    >
      <TrendIcon up={up} />
      {up ? "+" : ""}{value}%
    </span>
  )
}

function Card({ emphasis = false, children }) {
  return (
    <div
      className={
        "rounded-lg p-[22px] " +
        (emphasis
          ? "bg-coal shadow-sm"
          : "bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm shadow-sm")
      }
    >
      {children}
    </div>
  )
}

function Eyebrow({ emphasis, children }) {
  return (
    <div
      className={
        "text-[11px] font-semibold uppercase tracking-wider " +
        (emphasis ? "text-voltage-light" : "text-[color:var(--color-muted)] dark:text-muted-light")
      }
    >
      {children}
    </div>
  )
}

function Value({ emphasis, children }) {
  return (
    <div
      className={
        "font-display font-bold text-[28px] leading-none tracking-[-0.02em] truncate " +
        (emphasis ? "text-cloud" : "text-ink dark:text-cloud")
      }
    >
      {children}
    </div>
  )
}

function Sub({ children }) {
  return <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light truncate">{children}</span>
}

function SkeletonCard() {
  return (
    <Card>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
        &nbsp;
      </div>
      <div className="font-display font-bold text-[28px] leading-none text-ink dark:text-cloud mt-2 mb-2">·</div>
      <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">loading…</div>
    </Card>
  )
}

export default function StatCards({ scansSummary, loading }) {
  if (loading || !scansSummary) {
    return (
      <div className="grid grid-cols-2 min-[920px]:grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
      </div>
    )
  }

  const {
    total_lifetime = 0,
    code_count = 0,
    daily = [],
    week_total = 0,
    prev_week_total = 0,
    top_code = null,
  } = scansSummary

  const codeLabel = code_count === 1 ? "1 code" : `${code_count.toLocaleString()} codes`

  return (
    <div className="grid grid-cols-2 min-[920px]:grid-cols-4 gap-4">
      {/* Emphasis — scans this week */}
      <Card emphasis>
        <Eyebrow emphasis>Scans this week</Eyebrow>
        <div className="flex items-end justify-between gap-2 mt-2">
          <Value emphasis>{week_total.toLocaleString()}</Value>
          <div className="flex-shrink-0">
            <Sparkline data={daily.map((d) => d.count)} color="var(--color-voltage-light)" w={70} h={28} />
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <TrendPill now={week_total} prev={prev_week_total} />
          <span className="text-xs text-voltage-light/80">vs last week</span>
        </div>
      </Card>

      {/* Lifetime */}
      <Card>
        <Eyebrow>Lifetime scans</Eyebrow>
        <div className="mt-2 mb-2"><Value>{total_lifetime.toLocaleString()}</Value></div>
        <Sub>across {codeLabel}</Sub>
      </Card>

      {/* Top code — value is a label (text), so it uses a smaller, two-line
          treatment instead of the 28px numeric display size, which truncated
          most real labels at ~9 chars. */}
      <Card>
        <Eyebrow>Top code</Eyebrow>
        <div
          className="mt-2 mb-2 font-display font-bold text-[17px] leading-tight tracking-[-0.02em] text-ink dark:text-cloud line-clamp-2 break-words"
          title={top_code ? top_code.label : undefined}
        >
          {top_code ? top_code.label : "No scans yet"}
        </div>
        {top_code
          ? <Sub>{top_code.scan_count.toLocaleString()} scans</Sub>
          : <Sub>share a code to get started</Sub>}
      </Card>

      {/* Active codes */}
      <Card>
        <Eyebrow>Active codes</Eyebrow>
        <div className="mt-2 mb-2"><Value>{code_count.toLocaleString()}</Value></div>
        <Sub>in your library</Sub>
      </Card>
    </div>
  )
}
